import { useEffect, useRef, useState } from "react";
import type { LeaderboardView, RaceResult } from "@shared/types";
import { formatMs, formatPct } from "@shared/scoring";
import {
  recordGameFinish,
  recordRaceFinish,
  recordUsage,
  submitScore,
  type SubmitScoreResponse,
} from "../lib/api";
import {
  ensureServerProfile,
  getActiveProfile,
  hasSeenClaimNudge,
  markClaimNudgeSeen,
  type LocalProfile,
} from "../lib/profile";
import { recordRaceFinished } from "../lib/stats";
import ClaimProfileForm from "./ClaimProfileForm";
import ProfileChooser from "./ProfileChooser";
import RaceCar from "./brand/RaceCar";

interface Props {
  result: RaceResult;
  /** Server-signed result proof; when present, the score can be posted. */
  resultToken?: string;
  mode?: "solo" | "daily";
  /** True when this run beat the player's previous personal best (Pace Bunny). */
  newBest?: boolean;
  onPlayAgain: () => void;
  onHome: () => void;
  onViewLeaderboard?: (board: LeaderboardView) => void;
  onViewProfile?: (profile: LocalProfile) => void;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border-[3px] border-ink bg-white p-3 text-center shadow-[0_4px_0_#1c2a63]">
      <div className="font-display text-2xl tabular-nums text-ink">{value}</div>
      <div className="mt-0.5 text-xs font-bold uppercase tracking-wide text-ink/45">
        {label}
      </div>
    </div>
  );
}

export default function Results({
  result,
  resultToken,
  mode = "solo",
  newBest = false,
  onPlayAgain,
  onHome,
  onViewLeaderboard,
  onViewProfile,
}: Props) {
  const [copied, setCopied] = useState(false);
  const [active, setActive] = useState<LocalProfile | null>(() => getActiveProfile());
  const [guestName, setGuestName] = useState("");
  const [chooserOpen, setChooserOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState<SubmitScoreResponse | null>(null);
  const [postError, setPostError] = useState("");
  // One-time "save your racer" nudge, shown once after the first badge is earned.
  const [showNudge, setShowNudge] = useState(false);
  const [nudgeClosed, setNudgeClosed] = useState(false);

  // Count this completed race once: locally toward the "races finished" coin
  // badge, and as an aggregate game-finish for the admin engagement metric. The
  // ref guard makes it safe against StrictMode's double-invoked effect in dev;
  // Results remounts per race, so each finished race counts exactly once.
  const countedRef = useRef(false);
  useEffect(() => {
    if (countedRef.current) return;
    countedRef.current = true;
    recordRaceFinished();
    // `game` keeps its original meaning (solo/daily only) so the games-per-session
    // series stays continuous; `finish:<mode>` is the new per-mode measure.
    recordGameFinish();
    recordRaceFinish(mode);
  }, [mode]);

  const shareText =
    `🏁 BibleRacers — finished ${result.correctCount}/${result.finishLine} correct ` +
    `in ${formatMs(result.totalMs)} (${formatPct(result.accuracy)} accuracy, ` +
    `fastest ${formatMs(result.fastestMs)}). Can you beat me? ${location.origin}`;

  async function share() {
    try {
      if (navigator.share) {
        await navigator.share({ title: "BibleRacers", text: shareText });
        // Counted only on a completed share — the catch below is the cancel path.
        recordUsage("share:used");
        return;
      }
    } catch {
      /* user cancelled — fall through to copy */
    }
    try {
      await navigator.clipboard.writeText(shareText);
      recordUsage("share:used");
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked */
    }
  }

  async function postScore() {
    if (!resultToken || posting) return;
    setPosting(true);
    setPostError("");
    try {
      let token: string | undefined;
      let name: string;
      let linked: LocalProfile | null = null;
      if (active) {
        // Link to the active profile — minting its server identity if this is its
        // first save. Falls back to an unlinked post if the mint fails.
        const upgraded = await ensureServerProfile(active.localId).catch(() => active);
        setActive(upgraded);
        linked = upgraded;
        token = upgraded.playerToken ?? undefined;
        name = upgraded.displayName;
      } else {
        name = guestName.trim() || "Racer";
      }
      const res = await submitScore(resultToken, name, token);
      recordUsage("score:posted");
      setPosted(res);

      // Gentle, one-time nudge: right after a linked-but-unclaimed racer earns a
      // badge, invite them to save it so the badges can't be lost. Decided once
      // here (not per-render) and flagged so it never shows again.
      if (
        linked?.playerToken &&
        linked.playerId &&
        !linked.handle &&
        res.newBadges.length > 0 &&
        !hasSeenClaimNudge(linked.playerId)
      ) {
        markClaimNudgeSeen(linked.playerId);
        setShowNudge(true);
      }
    } catch (e) {
      setPostError(
        e instanceof Error ? e.message : "Couldn't post your score — try again.",
      );
    } finally {
      setPosting(false);
    }
  }

  const boardLabel = mode === "daily" ? "today's Daily Challenge" : "the leaderboard";
  const newBadges = posted?.newBadges ?? [];

  return (
    <div className="mx-auto w-full max-w-xl px-4 py-10">
      <div className="panel animate-pop-in p-6 text-center sm:p-8">
        <div className="mb-2 flex items-center justify-center">
          <RaceCar width={180} number={1} ariaLabel="Winning race car" />
        </div>
        <div className="mb-1 text-3xl">🏁🏆🏁</div>
        <h2 className="font-display text-3xl italic text-flame">Race complete!</h2>
        <p className="mt-1 font-semibold text-ink/60">
          {mode === "daily"
            ? "You finished today's Daily Challenge — here's how you raced."
            : "You crossed the finish line — here's how you raced."}
        </p>

        {newBest && (
          <div className="mt-4 animate-pop-in rounded-2xl border-[3px] border-ink bg-sunny/40 p-3">
            <div className="font-display text-xl text-ink">🐰💨 New best time!</div>
            <div className="text-sm font-bold text-ink/60">
              You out-raced the Pace Bunny!
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat label="Correct" value={`${result.correctCount}/${result.finishLine}`} />
          <Stat label="Accuracy" value={formatPct(result.accuracy)} />
          <Stat label="Total time" value={formatMs(result.totalMs)} />
          <Stat label="Avg / answer" value={formatMs(result.avgAnswerMs)} />
          <Stat label="Fastest" value={formatMs(result.fastestMs)} />
          <Stat label="Attempts" value={String(result.attempts)} />
        </div>

        {/* Leaderboard score submission */}
        {resultToken && (
          <div className="mt-6 rounded-2xl border-[3px] border-dashed border-ink/20 bg-sky-top/15 p-4">
            {posted ? (
              <div>
                <div className="font-display text-2xl text-ink">
                  {posted.rank
                    ? `🏆 You're #${posted.rank} of ${posted.total}!`
                    : "Score posted!"}
                </div>
                <p className="mt-1 text-sm font-semibold text-ink/60">
                  Your score is on {boardLabel}.
                </p>

                {newBadges.length > 0 && (
                  <div className="mt-3 rounded-2xl border-[3px] border-ink bg-sunny/30 p-3">
                    <div className="font-display text-lg text-ink">
                      🎉 New badge{newBadges.length > 1 ? "s" : ""}!
                    </div>
                    <div className="mt-2 flex flex-wrap justify-center gap-2">
                      {newBadges.map((b) => (
                        <span
                          key={b.id}
                          className="inline-flex items-center gap-1.5 rounded-full border-[3px] border-ink bg-white px-3 py-1 text-sm font-bold text-ink"
                          title={b.description}
                        >
                          <span aria-hidden>{b.emoji}</span> {b.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {showNudge && !nudgeClosed && active && (
                  <div className="mt-3 rounded-2xl border-[3px] border-ink bg-white p-4 text-left">
                    <ClaimProfileForm
                      profile={active}
                      heading="💾 Keep your badges safe!"
                      blurb={`Save ${active.displayName} with a secret recovery word so these badges can't be lost — and you can race as ${active.displayName} on any device.`}
                      cta="💾 Save my racer"
                      onClaimed={(h) => {
                        setActive((p) => (p ? { ...p, handle: h } : p));
                      }}
                    />
                    <button
                      type="button"
                      className="mt-2 w-full text-xs font-bold text-ink/50 hover:text-ink"
                      onClick={() => setNudgeClosed(true)}
                    >
                      {active.handle ? "Done" : "Maybe later"}
                    </button>
                  </div>
                )}

                <div className="mt-3 flex flex-wrap justify-center gap-2">
                  {onViewLeaderboard && (
                    <button
                      type="button"
                      className="btn-chunk bg-grape px-5 py-2.5 text-sm text-white"
                      onClick={() => onViewLeaderboard(posted.board)}
                    >
                      📊 See the leaderboard
                    </button>
                  )}
                  {onViewProfile && active && (
                    <button
                      type="button"
                      className="btn-chunk bg-white px-5 py-2.5 text-sm"
                      onClick={() => onViewProfile(active)}
                    >
                      ⭐ My profile
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-bold uppercase tracking-wide text-ink/70">
                    Post your score to {boardLabel}
                  </span>
                  <button
                    type="button"
                    className="shrink-0 rounded-full border-[3px] border-ink/15 bg-white px-2.5 py-1 text-sm font-bold text-ink hover:border-flame"
                    onClick={() => setChooserOpen(true)}
                  >
                    {active ? (
                      <>
                        <span aria-hidden>{active.avatar || "🙂"}</span> {active.displayName}{" "}
                        <span className="text-ink/40" aria-hidden>▾</span>
                      </>
                    ) : (
                      <>👤 Pick racer ▾</>
                    )}
                  </button>
                </div>

                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  {!active && (
                    <input
                      type="text"
                      value={guestName}
                      maxLength={16}
                      onChange={(e) => setGuestName(e.target.value)}
                      placeholder="Your racer name"
                      autoComplete="off"
                      className="min-w-0 flex-1 rounded-2xl border-[3px] border-ink bg-white px-4 py-2.5 font-semibold text-ink outline-none placeholder:text-ink/30 focus:border-flame"
                    />
                  )}
                  <button
                    type="button"
                    className={`btn-chunk bg-grass px-5 py-2.5 text-white ${
                      active ? "w-full" : ""
                    }`}
                    onClick={postScore}
                    disabled={posting}
                  >
                    {posting ? "Posting…" : "🏁 Post score"}
                  </button>
                </div>
                {!active && (
                  <p className="mt-2 text-left text-xs font-medium text-ink/45">
                    Use a nickname, not your real name — it shows on the public
                    leaderboard.{" "}
                    <a href="/privacy" className="font-bold text-grape underline">
                      Privacy
                    </a>
                  </p>
                )}
                {postError && (
                  <p className="mt-2 text-xs font-bold text-flame">{postError}</p>
                )}
              </div>
            )}
          </div>
        )}

        <div className="mt-7 grid gap-3 sm:grid-cols-2">
          <button type="button" className="btn-play" onClick={onPlayAgain}>
            <span aria-hidden>↻</span> RACE AGAIN
            <span className="sheen animate-shine" aria-hidden />
          </button>
          <button type="button" className="btn-chunk bg-grape py-4 text-white" onClick={share}>
            {copied ? "Copied! ✓" : "🔗 Share result"}
          </button>
        </div>
        <button
          type="button"
          className="mt-4 text-sm font-bold text-ink/55 hover:text-ink"
          onClick={onHome}
        >
          ← Change settings
        </button>
      </div>

      {chooserOpen && (
        <ProfileChooser
          onClose={() => setChooserOpen(false)}
          onChange={(p) => setActive(p)}
          onViewProfile={onViewProfile}
        />
      )}
    </div>
  );
}
